import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Button from '../Button'
import SearchTextBox from '../SearchTextBox'
import styles from './styles.module.scss'
import { commonActions } from 'srcReact/components'
import InterestSuggetionSuccess from 'srcReact/components/popinsContent/InterestSuggetionSuccess'
import Spinner from 'srcReact/components/common2/Spinner'
import Pill from 'srcReact/components/common2/Pill'
import useInfinitScroll from 'srcReact/helpers/hooks/useInfinitScroll'
import { isEmpty } from 'srcReact/helpers/validator'
import usePrevious from 'srcReact/helpers/hooks/usePrevious'
import Popup from 'reactjs-popup'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import ErrorMessage from '../ErrorMessage'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'
import ProfileChallengeText from '../ProfileChallengeText'

export default function InterestPicker({
  componentType = 'interest',
  origin,
  entityId, //userId for profile, groupId for group, mixrId for mixr
  userEmail, //for registration page
  containerCss,
  isMobile,
  onInterestChange,
  isLoggedIn = 1,
  primaryButton,
  secondaryButton,
  existingInterests,
  showAddText = true,
  noMaxInterestsCount = false,
  clearSelectedInterests = false,
  priorityInterests = [],
  isMixrInterests = false,
  isChallenge = false,
  minInterestsCount = appConstantSettings.MIN_INTEREST_COUNT,
  maxInterestsCount = appConstantSettings.MAX_INTEREST_COUNT
}) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const selectedInterestsRef = useRef(null)

  const interestSuggestionsWrapperRef = useRef(null)

  const {
    common: {
      isNative,
      isDesktop,
      interests: { stats, list },
      interestsSearchLoading,
      suggestedInterest,
      suggestInterestLoading,
      selectedInterests,
      interestError
    }
  } = useSelector((state) => state)
  const prevSelectedInterests = usePrevious(selectedInterests)
  //states
  const [pagination, setPagination] = useState({
    nextOffset: 0,
    isPagination: false
  })
  const [search, setSearch] = useState('')
  const [error, setError] = useState('')
  const [openSuggestInterest, setOpenSuggestInterest] = useState(false)
  const [hasExactMatch, setHasExactMatch] = useState(false)
  const [suggestTriggered, setSuggestTriggered] = useState(false)

  useEffect(() => {
    setOpenSuggestInterest(false)
    setError('')
  }, [])
  useEffect(() => {
    setOpenSuggestInterest(false)
    setError('')
    getInterests(0)
  }, [componentType])

  useEffect(() => {
    if (suggestedInterest && suggestedInterest.interest && suggestedInterest.interest.id > 0 && suggestTriggered) {
      setSuggestTriggered(false)
      setOpenSuggestInterest(true)
      addSelectedInterest(suggestedInterest.interest)
      setSearch('')
    }
  }, [suggestedInterest && suggestedInterest.interest])

  useEffect(() => {
    if ((existingInterests && existingInterests.length > 0) || clearSelectedInterests) {
      dispatch(commonActions.updateSelectedInterests(existingInterests))
    }
  }, [existingInterests])

  useEffect(() => {
    if (!interestsSearchLoading && search.length > 0 && !interestError && list?.length > 0) {
      const exactInterest = list.some((x) => x.name.toLowerCase() === search.toLowerCase())
      setHasExactMatch(exactInterest)
    }
  }, [list, interestError, interestsSearchLoading, search])

  const interestsScrollContainer = useRef()
  const ref = useInfinitScroll(
    interestsSearchLoading,
    stats && stats.remaining,
    stats && stats.nextOffset,
    setPagination,
    [interestsSearchLoading]
  )

  useEffect(() => {
    getInterests(pagination.nextOffset)
  }, [pagination, search])

  useEffect(() => {
    if (priorityInterests?.length > 0) {
      getInterests(pagination.nextOffset)
    }
  }, [priorityInterests])

  const getInterests = (offset) => {
    let selectedInterstsIds = selectedInterests?.length > 0 ? selectedInterests.map((i) => i.id).join(',') : ''
    if (!selectedInterstsIds) {
      selectedInterstsIds = existingInterests?.length > 0 ? existingInterests.map((i) => i.id).join(',') : ''
    }

    const priorityInterestIds = priorityInterests?.length > 0 ? priorityInterests.map((i) => i.id).join(',') : ''
    dispatch(
      commonActions.searchInterest(componentType, search, offset, selectedInterstsIds + priorityInterestIds, isLoggedIn)
    )
  }

  useEffect(() => {
    //scroll to end of selected interest list, if new interest is added as new interest is added at the end
    //if any interest is removed from the selected list, do not scroll
    let prevCount = !isEmpty(prevSelectedInterests) && prevSelectedInterests.length
    let selectedCount = !isEmpty(selectedInterests) && selectedInterests.length
    if (prevCount < selectedCount) {
      selectedInterestsRef.current.scrollTop = selectedInterestsRef.current.scrollHeight
    }
  }, [selectedInterests])

  const handleSearch = (searchValue) => {
    if (searchValue.length >= 3 || searchValue.length === 0) {
      setPagination({
        nextOffset: 0,
        isPagination: false
      })
      setSearch(searchValue)
    }
  }

  const addSelectedInterest = (interest) => {
    // clearSelectedInterests on create-mix page -- if user chose mixr interests and selected interests are empty
    if (selectedInterests || clearSelectedInterests) {
      setError('')
      const isInterestAlreadySelected = selectedInterests && selectedInterests.some((i) => i.id === interest.id)
      const isSizeExceed =
        selectedInterests && selectedInterests.length >= maxInterestsCount && noMaxInterestsCount === false
          ? true
          : false
      if (isInterestAlreadySelected) {
        setError(
          t('interestPicker.interestExistsError', {
            interest: interest.name
          })
        )
      } else if (isSizeExceed) {
        setError(t('interestPicker.maxInterestsError', { max: maxInterestsCount }))
      } else {
        let updatedInterests = selectedInterests ? [...selectedInterests, interest] : [interest]
        dispatch(commonActions.updateSelectedInterests(updatedInterests))
        dispatch(commonActions.updateInterestsAfterSelection(interest, 'remove'))

        if (onInterestChange) {
          onInterestChange(updatedInterests)
        }
        if (search && search.length > 0) {
          setSearch('')
        }
      }
    }
  }
  const removeSelectedInterest = (e, interest) => {
    e.preventDefault()
    let updatedInterests = [...selectedInterests?.filter((i) => i.id !== interest.id)]
    if (onInterestChange) {
      onInterestChange(updatedInterests)
    }
    setError('')
    dispatch(commonActions.updateSelectedInterests(updatedInterests))
    dispatch(commonActions.updateInterestsAfterSelection(interest, 'add'))
  }
  const markForRemoval = (interest) => {
    const updatedInterests = selectedInterests?.map((i) => {
      if (i.id === interest.id) {
        return { ...i, selectedForRemoval: true }
      }
      return i
    })
    dispatch(commonActions.updateSelectedInterests(updatedInterests))
  }
  const suggestInterest = () => {
    if (search && search.length > 0) {
      const orgId = getCurrentOrgId()
      setSuggestTriggered(true)
      dispatch(commonActions.suggestInterest(componentType, search, origin, orgId, userEmail, entityId, isLoggedIn))
    }
  }

  const closeModal = () => {
    setOpenSuggestInterest(false)
  }

  const unMarkInterests = () => {
    const updatedInterests = selectedInterests?.map((interest) => {
      if (interest.selectedForRemoval === true) {
        return { ...interest, selectedForRemoval: false }
      }
      return interest
    })
    dispatch(commonActions.updateSelectedInterests(updatedInterests))
  }

  const handleKeyUp = (e) => {
    if (e.keyCode === 13 || e.keyCode === 32) {
      interestsScrollContainer.current.firstChild.focus()
    } else if (e.keyCode === 9) {
      // if (editMode) {
      //   if (cancelButtonRef && cancelButtonRef.current) {
      //     cancelButtonRef.current.focus()
      //   } else {
      //     const locationContainer = document.querySelector('#locationContainer')
      //     console.log('locationContainer:', locationContainer)
      //     if (locationContainer) {
      //       locationContainer.focus()
      //     }
      //   }
      // } else {
      //   nextButtonRef.current.focus()
      // }
    }
  }

  const handlePillKeyUp = (e) => {
    if (e.keyCode === 27) {
      interestSuggestionsWrapperRef.current.focus()
    }
  }

  const validate = () => {
    if (
      selectedInterests &&
      (noMaxInterestsCount === false
        ? selectedInterests.length < minInterestsCount || selectedInterests?.length > maxInterestsCount
        : selectedInterests.length < minInterestsCount)
    ) {
      if (noMaxInterestsCount) {
        setError(t('interestPicker.info', { count: minInterestsCount, type: componentType }))
      } else {
        setError(
          t('interestPicker.minMaxInterestError', {
            min: minInterestsCount,
            max: maxInterestsCount,
            type:
              componentType === 'expertise'
                ? t('formlabel.expertise', { count: 0 }).toLowerCase()
                : t('formlabel.interest', { count: 0 }).toLowerCase()
          })
        )
      }
      return false
    }
    return true
  }

  const renderSuggestContainer = (hasItems) => {
    return (
      <div
        className={`${styles.suggestionWrapper} ${
          !hasExactMatch && hasItems && styles.suggestionWrapperWithInterests
        }`}>
        <p
          className={styles.suggestionInfo}
          dangerouslySetInnerHTML={{
            __html: `${t('interestPicker.seachedNoInterest1', {
              seachedInterest: search,
              type:
                componentType === 'expertise'
                  ? t('formlabel.expertise', { count: 1 }).toLowerCase()
                  : t('formlabel.interest', { count: 1 }).toLowerCase()
            })} ${
              !hasExactMatch && hasItems && componentType == 'interest' ? t('interestPicker.seachedNoInterest2') : ''
            }`
          }}
        />
        {componentType === 'interest' && (
          <Button
            text={suggestInterestLoading ? <Spinner isWhite={true} /> : t('interestPicker.btnSuggestInterest')}
            size='medium'
            handleClick={suggestInterest}
          />
        )}
      </div>
    )
  }
  return (
    <React.Fragment>
      <div className={`${styles.interestsWrapper} ${containerCss}`}>
        {showAddText && selectedInterests?.length === 0 ? (
          <h3 className={`${styles.para}`}>
            {t('interestPicker.info', {
              count: minInterestsCount,
              type:
                componentType === 'expertise'
                  ? t('formlabel.expertise', { count: minInterestsCount }).toLowerCase()
                  : t('formlabel.interest', { count: minInterestsCount }).toLowerCase()
            })}
          </h3>
        ) : (
          ''
        )}
        {isChallenge &&
          ((componentType === 'interest' && selectedInterests?.length < appConstantSettings.CHALLENGE_INTERESTS) ||
            (componentType === 'expertise' && selectedInterests?.length < appConstantSettings.CHALLENGE_EXPERTISE)) && (
            <ProfileChallengeText
              className={styles.challengeText}
              text={
                componentType === 'interest'
                  ? t('my_profile.info_block_edit.interest.challenge', {
                      count: appConstantSettings.CHALLENGE_INTERESTS
                    })
                  : t('my_profile.info_block_edit.interest.expChallenge', {
                      count: appConstantSettings.CHALLENGE_EXPERTISE
                    })
              }
            />
          )}
        {origin === 'profile' && selectedInterests?.length > 0 && (
          <h3 className={styles.subTitle}>
            {t(`interestPicker.${componentType === 'interest' ? 'modalSubTitle1Int' : 'modalSubTitle1Exp'}`, {
              count: selectedInterests?.length > 0 ? ` (${selectedInterests.length})` : ''
            })}
          </h3>
        )}

        <div
          ref={selectedInterestsRef}
          className={`${styles.selectedInterests} ${
            origin === 'profile' && !isDesktop ? styles.selectedInterestsProfile : ''
          }`}>
          {selectedInterests?.length > 0 &&
            selectedInterests.map((interest, index) =>
              interest.selectedForRemoval ? (
                <Pill
                  type={componentType}
                  dataId={interest.id}
                  onClick={(e) => removeSelectedInterest(e, interest)}
                  onMouseLeave={() => unMarkInterests()}
                  selectedForRemoval={true}
                  className={componentType === 'expertise' && styles.selectedForRemovalExp}
                  selected={false}
                  key={`${interest.id}_${index}`}
                  text={interest.name}
                />
              ) : (
                <Pill
                  type={componentType}
                  dataId={interest.id}
                  onMouseEnter={() => markForRemoval(interest)}
                  onClick={() => markForRemoval(interest)}
                  selected={true}
                  selectedForRemoval={false}
                  key={`${interest.id}_${index}`}
                  text={interest.name}
                />
              )
            )}
        </div>
        {error && error.length > 0 && <ErrorMessage text={error} className={styles.error} />}
        {origin === 'profile' && (
          <h3 className={styles.subTitle}>
            {componentType === 'interest'
              ? t('interestPicker.modalSubTitle2Int')
              : t('interestPicker.modalSubTitle2Exp')}
          </h3>
        )}
        <SearchTextBox
          value={search}
          {...(search.length === 0 && { clear: true })}
          minimumSearchChar={3}
          size='large'
          iconPosition='start'
          placeholder={
            componentType === 'expertise'
              ? t('interestPicker.searchExprtPlaceholder')
              : t('interestPicker.searchPlaceholder')
          }
          handleSearch={handleSearch}
          cssClass={styles.searchCss}
          inputCssClass={styles.searchInputCss}
          buttonCssClass={isNative && styles.buttonCssClass}
          iconCSS={`${styles.iconCss} ${search.length > 0 && styles.focused}`}
          style='secondary'
          // handleKeyDown={handleSearch}
          // errorMessage={t('group.createGroup.interest.searchCharError', { character_count: 3 })}
        />

        <div
          tabIndex={0}
          ref={interestSuggestionsWrapperRef}
          onKeyUp={handleKeyUp}
          className={`${styles.interestSuggestionsWrapper} ${
            origin === 'profile' && !isDesktop ? styles.interestSuggestionsWrapperProfile : ''
          }`}>
          {/* below spinner will display on search text change */}
          {interestsSearchLoading ||
            (interestError && search.length > 0 && list.length === 0 && (
              <div className={styles.spinnerWrapper2}>
                <Spinner />
              </div>
            ))}
          <div className={styles.interestSuggestions} ref={interestsScrollContainer}>
            {isMixrInterests &&
              priorityInterests.map((interest, index) => {
                const isAlreadSelected = selectedInterests?.find((int) => int.id === interest.id)
                return !isAlreadSelected ? (
                  <Pill
                    type={componentType}
                    dataId={interest.id}
                    onClick={() => addSelectedInterest(interest, true)}
                    key={`${interest.id}_${index}`}
                    text={interest.name}
                    onKeyUp={handlePillKeyUp}
                  />
                ) : null
              })}
            {list &&
              (list.length > 0 && !interestError ? (
                <React.Fragment>
                  {!interestsSearchLoading &&
                    search.length > 0 &&
                    !interestError &&
                    !hasExactMatch &&
                    renderSuggestContainer(true)}
                  {list.map((interest, index) => {
                    const isLastInterest = index === list.length - 1
                    let isPriorityInterest = false
                    if (priorityInterests?.length > 0) {
                      isPriorityInterest = priorityInterests.find((int) => int.id === interest.id)
                    }
                    return !isPriorityInterest ? (
                      <Pill
                        type={componentType}
                        dataId={interest.id}
                        lastInterestRef={isLastInterest ? ref : null}
                        onClick={() => addSelectedInterest(interest)}
                        key={`${interest.id}_${index}`}
                        text={interest.name}
                        onKeyUp={handlePillKeyUp}
                      />
                    ) : null
                  })}
                </React.Fragment>
              ) : !interestsSearchLoading && search.length > 0 && !interestError ? (
                renderSuggestContainer(false)
              ) : null)}
          </div>
          {/* below spinner will display on first interest load or infinite scrolling */}
          {search.length === 0 && interestsSearchLoading && !interestError && (
            <div className={list.length === 0 ? styles.spinnerWrapper2 : styles.spinnerWrapper}>
              <Spinner />
            </div>
          )}
        </div>
        <div className={styles.buttonContainer}>
          {secondaryButton && (
            <Button
              type='button'
              customStyle={styles.secondaryButtonStyle}
              text={secondaryButton.text}
              size={secondaryButton.size}
              style='secondary'
              handleClick={secondaryButton.clickHandler}
            />
          )}

          {primaryButton && (
            <Button
              disabled={primaryButton.disabled ? primaryButton.disabled : false}
              type='button'
              text={primaryButton.text}
              size={primaryButton.size}
              handleClick={() => {
                const isValid = validate()
                if (isValid) {
                  primaryButton.clickHandler()
                }
              }}
            />
          )}
        </div>
      </div>

      <Popup
        contentStyle={{ borderRadius: '1.375rem', width: isMobile && '100%', maxWidth: '32.5rem' }}
        onClose={closeModal}
        open={openSuggestInterest}>
        <InterestSuggetionSuccess closeModal={closeModal} />
      </Popup>
    </React.Fragment>
  )
}
